import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { formatDate, termLabel, usaCurrency } from '../../../utils/helpers';
import {
  dateColumnOperators,
  standardDateOperators,
  standardOperators,
  tagsOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getOriginLabel, isConvertedRecord, isUpstackSourced } from '../../../utils/originLabel';
import { UpstackSourcedRenderer } from '../../Compounds/UpstackSourcedRenderer';
import { getSupplierById } from '../../../store/Supplier';
import { getUserAccountById } from '../../../store/User';
import {
  compareContractEndDates,
  formatContractEndDate,
  contractEndDateComparator,
  contractEndDateFilters
} from './helpers';
import { CardButton } from '../../Compounds/Buttons/CardButton';
import { InventoryAsset } from '../../../types';
import { addressComponentColumns } from '../../Compounds/UpstackDataGrid/addressColumns';
import { tagsColumn } from '../../Compounds/UpstackDataGrid/tagColumns';
import type { TagEntityRefresh } from '../../Compounds/Tagging/Tags';
import { EntityTagRenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/EntityTagRenderer';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import type { SupportedTagEntities } from '../../../store/Tags';
import { SupportedTagEntityTypes } from '../../../store/tagEntityTypes';
import { productLabel } from '../../../utils/productLabel';

// Lazy on purpose: a static import closes the col-def ↔ store/User cycle and hands out a half-built store.
const refetchInventory: TagEntityRefresh = async (id) =>
  (await import('../../../store/Inventory')).fetchInventoryAssetById(id);

// The aggregate grid mixes Salesforce assets with customer-entered rows; only the latter have a schema id.
const inventoryTagEntityType = (row: SupportedTagEntities): SupportedTagEntityTypes =>
  (row as InventoryAsset).outside_inventory_schema_id
    ? SupportedTagEntityTypes.OUTSIDE_INVENTORY
    : SupportedTagEntityTypes.ASSET;

// Same split as the entity type: a synced asset names its account in Salesforce's id space, a customer-entered row in the portal's.
export const inventoryRowAccountId = (row: SupportedTagEntities | Partial<InventoryAsset>): string | undefined =>
  (row as InventoryAsset).account_upstack_id ?? (row as InventoryAsset).account_id ?? undefined;

// Exported so a grid borrowing this catalog can rebuild the column against its own row source's refresh.
export const inventoryTagsColumn = (refetch: TagEntityRefresh): GroupedGridColDef =>
  tagsColumn({
    filterOperators: tagsOperators(),
    renderer: EntityTagRenderer,
    entityType: inventoryTagEntityType,
    entityAccountId: inventoryRowAccountId,
    refetch,
    columnGroup: 'Admin'
  });

const wideTextColumn = (field: string, headerName: string, columnGroup: string): GroupedGridColDef => ({
  field,
  headerName,
  sortable: true,
  width: 400,
  filterable: true,
  type: 'singleSelect',
  valueOptions: [],
  filterOperators: standardOperators(),
  description: 'Drag to move column',
  columnGroup
});

export const ColumnGroupSortOrder = [
  'Location',
  'Admin',
  'Network',
  'Rack & Power',
  'Install Location',
  'Voice Services',
  'Ungrouped'
];

/**
 * Canonical list of inventory grid columns. The `field` of each entry is the
 * stable id used by:
 *  - The UI's column visibility/disable toggles
 *  - `AccountCustomization#content.inventory_grid.disabledFields` in upstack_api
 *  - The MCP customization tools (`portal_admin_{create,update}_account_customization`)
 *    advertise this set via their `tools/list` input schemas.
 *
 * Cross-repo coupling: when adding, renaming, or removing a column here, mirror
 * the change in upstack_api at `app/models/account_customization.rb`
 * (`AccountCustomization::INVENTORY_GRID_FIELDS`) and bump the count assertion
 * in `spec/models/account_customization_spec.rb`. See `docs/mcp.md` in
 * upstack_api for the protocol.
 *
 * Exception: the five address-component columns (addressInfo.streetAddress/city/state/
 * zip/country) are the only ones NOT mirrored in INVENTORY_GRID_FIELDS, though the admin
 * form can disable them too, so they don't count toward that assertion. Every other column
 * is mirrored, off-by-default ones such as `assetNumber` included.
 */
export const DefaultInventoryGridColumnDefs: GroupedGridColDef[] = [
  {
    field: 'details',
    headerName: 'Details',
    sortable: false,
    width: 65,
    filterable: false,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
      />
    ),
    hideable: false,
    disableReorder: true,
    columnGroup: 'Admin'
  },
  {
    field: 'origin',
    headerName: 'Origin',
    sortable: true,
    renderCell: (params: GridCellParams) => (
      <UpstackSourcedRenderer
        origin={isUpstackSourced(params.row)}
        converted={isConvertedRecord(params.row.entry_method)}
        accountName={params.row.accountName || getUserAccountById(params.row.account_id)?.name}
      />
    ),
    width: 125,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueGetter: (value, row) =>
      getOriginLabel(
        isUpstackSourced(row),
        isConvertedRecord(row?.entry_method),
        row?.accountName || getUserAccountById(row?.account_id)?.name
      ),
    filterOperators: standardOperators(),
    columnGroup: 'Admin'
  },
  {
    field: 'account_id',
    headerName: 'Account',
    sortable: true,
    width: 300,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value, row) => {
      return row?.accountName || getUserAccountById(row.account_id)?.name || row.account_id;
    },
    valueGetter: (value, row) => row?.accountName || getUserAccountById(row?.account_id)?.name || value,
    disableReorder: true,
    filterable: true,
    filterOperators: standardOperators(),
    columnGroup: 'Admin'
  },
  {
    field: 'sfId',
    headerName: 'Id',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueGetter: (value, row) => (row?.origin ? row?.sfId : row?.id),
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'assetNumber',
    headerName: 'Asset Number',
    sortable: true,
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin',
    defaultHidden: true
  },
  {
    field: 'addressInfo.number',
    headerName: 'Location Code',
    sortable: true,
    width: 200,
    filterable: true,
    valueGetter: (value, row) => row?.addressInfo?.number,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Location'
  },
  {
    field: 'customerLocationName',
    headerName: 'Location Name',
    sortable: true,
    width: 200,
    filterable: true,
    valueGetter: (value, row) => row?.customerLocationName,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Location'
  },
  {
    field: 'customerLabelOther',
    headerName: 'Location Label',
    sortable: true,
    width: 200,
    filterable: true,
    valueGetter: (value, row) => row?.customerLabelOther,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Location'
  },
  {
    field: 'address',
    headerName: 'Address',
    sortable: true,
    width: 300,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Location'
  },
  ...addressComponentColumns(standardOperators(), 'Location'),
  {
    field: 'productDisplay',
    headerName: 'Product',
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueGetter: (value, row) => {
      if (value) return value;
      if (!row?.product) return undefined;
      return productLabel(row.product);
    },
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'supplier.name',
    headerName: 'Supplier',
    valueGetter: (value, row) => row?.supplier?.name,
    sortable: true,
    width: 112,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'accessProvider',
    headerName: 'Access Provider',
    sortable: true,
    width: 140,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value, row) => {
      if (row.origin) return value;
      return getSupplierById(row.accessProvider)?.name || row.accessProvider;
    },
    valueGetter: (value, row) => {
      if (row.origin) return value;
      return getSupplierById(row.accessProvider)?.name || row.accessProvider;
    },
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'mRC',
    headerName: 'Monthly Cost',
    sortable: true,
    valueFormatter: (value) => {
      return `${usaCurrency.format(value)}`;
    },
    valueGetter: (value) => Number(value),
    width: 125,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'nRC',
    headerName: 'One-Time Cost',
    sortable: true,
    valueFormatter: (value) => {
      return `${usaCurrency.format(value)}`;
    },
    valueGetter: (value) => Number(value),
    width: 125,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'term',
    headerName: 'Term Length',
    sortable: true,
    // A getter rather than a formatter: filtering and sorting compare this value, and two
    // spellings of month-to-month reach the grid from Salesforce and from outside inventory.
    valueGetter: (value: string) => termLabel(value),
    width: 100,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'completionDate',
    headerName: 'Install Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: dateColumnOperators(),
    width: 190,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'serviceActivationDate',
    headerName: 'Activation Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: dateColumnOperators(),
    width: 190,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'contractEndDate',
    headerName: 'Est. Contract End Date',
    sortable: true,
    valueGetter: (value, row) => formatContractEndDate(value, row.term),
    valueFormatter: (value, row) => {
      return formatContractEndDate(value, row.term);
    },
    sortComparator: contractEndDateComparator,
    valueOptionsComparator: compareContractEndDates,
    filterOperators: contractEndDateFilters(),
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'providerAccountNumber',
    headerName: 'Account Number',
    sortable: true,
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'supplierOrderNumber',
    headerName: 'Supplier Order #',
    sortable: true,
    width: 160,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'disconnectedReason',
    headerName: 'Disconnected Reason',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'speed',
    headerName: 'Speed',
    sortable: true,
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'circuitIDs',
    headerName: 'Circuit ID',
    sortable: true,
    width: 350,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'lecCircuitID',
    headerName: 'LEC Circuit ID',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'iPDetails',
    headerName: 'IP Addresses',
    sortable: true,
    width: 600,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'rackType',
    headerName: 'Rack Type',
    sortable: true,
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Rack & Power'
  },
  {
    field: 'kwRack',
    headerName: 'kW Rack',
    sortable: true,
    width: 135,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Rack & Power'
  },
  {
    field: 'locationALecCircuitId',
    headerName: 'Location A Circuit ID',
    sortable: true,
    width: 260,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationALecDemarc',
    headerName: 'Location A Demarc',
    sortable: true,
    width: 250,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationALocalLec',
    headerName: 'Location A Local LEC',
    sortable: true,
    width: 100,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationAText',
    headerName: 'Location A',
    sortable: true,
    width: 100,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationZLecCircuitId',
    headerName: 'Location Z Circuit ID',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationZLecDemarc',
    headerName: 'Location Z Demarc',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationZLocalLec',
    headerName: 'Location Z Local LEC',
    sortable: true,
    width: 120,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'locationZText',
    headerName: 'Location Z',
    sortable: true,
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'equipmentManufacturer',
    headerName: 'Equipment Manufacturer',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'disconnectedDate',
    headerName: 'Disconnected Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: dateColumnOperators(),
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'lecDemarc',
    headerName: 'Demarcation Details',
    sortable: true,
    width: 100,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'demarcationDevice',
    headerName: 'Demarcation Device',
    sortable: true,
    width: 100,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'line_item_count',
    headerName: 'Line Items',
    sortable: true,
    width: 160,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'enclosure',
    headerName: 'Enclosure',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Install Location'
  },
  {
    field: 'floor',
    headerName: 'Floor',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Install Location'
  },
  {
    field: 'focDate',
    headerName: 'FOC Date',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardDateOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'licenseQuantity',
    headerName: 'License Qty.',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'licenseExpirationDate',
    headerName: 'License End Date',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardDateOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'metered',
    headerName: 'Metered Power',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Rack & Power'
  },
  {
    field: 'model',
    headerName: 'Model',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Rack & Power'
  },
  {
    field: 'mount',
    headerName: 'Install Mount',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Install Location'
  },
  {
    field: 'networkType',
    headerName: 'Network Type',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'pduPhase',
    headerName: 'PDU Phase',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Rack & Power'
  },
  {
    field: 'powerType',
    headerName: 'Power Type',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Rack & Power'
  },
  {
    field: 'circuitRole',
    headerName: 'Circuit Role',
    sortable: true,
    width: 350,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Network'
  },
  {
    field: 'renewalLanguage',
    headerName: 'Renewal Language',
    sortable: true,
    width: 350,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'voiceType',
    headerName: 'Voice Type',
    sortable: true,
    width: 350,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Voice Services'
  },
  {
    field: 'supplierSupportURL',
    headerName: 'Supplier Support URL',
    sortable: true,
    width: 200,
    filterable: true,
    renderCell: (params: GridCellParams) => {
      const url = params.value as string;
      if (url) {
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer">
            {url}
          </a>
        );
      }
      return null;
    },
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  wideTextColumn('networkDetails', 'Network Details', 'Network'),
  wideTextColumn('deviceDetails', 'Device Details', 'Network'),
  inventoryTagsColumn(refetchInventory)
];

export const SupportNumberDefaults: GroupedGridColDef[] = [
  {
    field: 'productSupportNumber',
    headerName: 'Supplier Tech Support',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  },
  {
    field: 'supplierSupportNumber',
    headerName: 'Supplier General Support',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  }
];

export const CreateInternalDetailsColumn = (openDrawer: (asset: InventoryAsset) => void): GridColDef[] => {
  return [
    {
      type: 'actions',
      field: 'details',
      headerName: 'Details',
      sortable: false,
      width: 90,
      filterable: false,
      renderCell: (params) => (
        <div className="mt-3">
          <div className="desc-wrap">{params.formattedValue || params.value}</div>
          <CardButton
            data-testid="detail-cta-button"
            data-cy="detail-cta-button"
            onClick={() => openDrawer(params.row)}
            className="h-4"
            icon="details"
          />
        </div>
      )
    }
  ];
};

export const OutsideInventoryDefaults: GroupedGridColDef[] = [
  {
    field: 'upstack_support_managed',
    headerName: 'Support Level',
    sortable: true,
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueGetter: (value) => (value ? 'Supported' : 'Not Supported'),
    valueOptions: ['Supported', 'Not Supported'],
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    columnGroup: 'Admin'
  }
];

export { createCustomFieldColumns } from '../../Compounds/UpstackDataGrid/customFieldColumns';
